import { useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { useToggle } from '@/hooks/useToggle';
import { scrollIntoView } from '@/utils/dom';
import Card from '@/components/Card';
import { CloseIcon, FileIcon, FolderIcon } from '@/icons';
import EntryNode from './EntryNode';
import type { StagedEntry } from '../types';
import { classes, getEntrySubtitle, styles } from './utils';

export type StagedEntryRowProps = {
  entry: StagedEntry;
  onPreview?: (file: File) => void;
  onRemove?: (entry: StagedEntry) => void;
  onRemoveNested?: (entryUuid: string, path: string[]) => void;
};

export default function StagedEntryRow({
  entry,
  onPreview,
  onRemove,
  onRemoveNested,
}: StagedEntryRowProps) {
  const childrenRef = useRef<HTMLDivElement>(null);
  const [isExpanded, toggleExpanded] = useToggle();
  const file = entry.contents instanceof File ? entry.contents : undefined;

  useEffect(() => {
    // Reveal the children when expanding a folder that sits below the fold.
    if (isExpanded) scrollIntoView(childrenRef.current, { block: 'nearest' });
  }, [isExpanded]);

  const handleContentClick = () => {
    if (file) return onPreview?.(file);
    toggleExpanded();
  };

  const handleRemoveChild = (path: string[]) => {
    onRemoveNested?.(entry.uuid, path);
  };

  const renderIcon = () => {
    if (entry.previewSource) {
      return (
        <img
          className={classes.rowImg}
          src={entry.previewSource}
          alt={entry.name}
        />
      );
    }
    return file ? (
      <FileIcon className={classes.rowIcon} />
    ) : (
      <FolderIcon className={classes.rowIcon} />
    );
  };

  const renderChildren = () => {
    if (entry.contents instanceof File || !isExpanded) return null;
    return (
      <div ref={childrenRef} className={classes.children}>
        {Object.entries(entry.contents).map(([name, contents]) => (
          <EntryNode
            key={name}
            contents={contents}
            name={name}
            path={[name]}
            onPreview={onPreview}
            onRemove={handleRemoveChild}
          />
        ))}
      </div>
    );
  };

  return (
    <div className={classes.entry}>
      <Card sx={styles.row} className={classes.row}>
        <button
          className={twMerge(classes.rowContent, classes.rowButton)}
          type="button"
          onClick={handleContentClick}>
          <div className={classes.rowIconWrapper}>{renderIcon()}</div>
          <div className={classes.rowDetails}>
            <span className={classes.rowName}>{entry.name}</span>
            <span className={classes.rowSubtitle}>
              {getEntrySubtitle(entry)}
            </span>
          </div>
        </button>
        {onRemove && (
          <CloseIcon
            className={classes.removeIcon}
            onClick={() => onRemove(entry)}
          />
        )}
      </Card>
      {renderChildren()}
    </div>
  );
}
